import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  createContext
} from 'react';

import { Logger } from 'wtb-utils';
import { useAppContext, useDialogNavigationContext, useTransactionContext } from 'context';
import {
  ANALYTICS_EVENTS,
  APP_STEPS,
  AUTOGIRO_STEPS,
  BETALINGSSERVICE_STEPS,
  INVOICE_STEPS,
  SWISH_STEPS,
  PAYMENT_TYPES
} from 'constants.js';
import { useSendAnalyticsEvent } from 'hooks';

const PaymentContext = createContext();

const PaymentProvider = ({ children, initialState = {} }) => {
  const { setAppStep } = useAppContext();
  const { setBack, setDialogNavigationPath } = useDialogNavigationContext();
  const { selectedPaymentPlan } = useTransactionContext();

  const sendAnalyticsEvent = useSendAnalyticsEvent();

  const [ swishError, setSwishError ] = useState(initialState.swishError || null);

  useEffect(() => {
    if (!selectedPaymentPlan) {
      setBack(null);
    }
  }, [ setBack, selectedPaymentPlan ]);

  const setStep = useCallback((step) => {
    if (!selectedPaymentPlan) {
      setAppStep(APP_STEPS.PAYMENT);
      Logger.error('A selected payment plan is needed before changing payment step');
      return;
    }

    // TODO: Remove when we remove avtalegiro mock
    const payment = selectedPaymentPlan.type.replace('_MOCK', '').replace('_mock', '').toLowerCase();
    setDialogNavigationPath(`payment/${payment}/${step}`);

    // Send corresponding analytics event
    switch (payment.toUpperCase()) {
      case PAYMENT_TYPES.AUTOGIRO: {
        switch (step) {
          case AUTOGIRO_STEPS.ADD_BANK_ACCOUNT:
            sendAnalyticsEvent(ANALYTICS_EVENTS.AUTOGIRO.ADD_BANK_ACCOUNT);
            break;
          case AUTOGIRO_STEPS.CONNECTING_BANK:
            sendAnalyticsEvent(ANALYTICS_EVENTS.AUTOGIRO.CONNECTING_BANK);
            break;
          case AUTOGIRO_STEPS.ACCOUNT_SELECTION:
            sendAnalyticsEvent(ANALYTICS_EVENTS.AUTOGIRO.ACCOUNT_SELECTION);
            break;
          case AUTOGIRO_STEPS.MAIN_BANKS:
            sendAnalyticsEvent(ANALYTICS_EVENTS.AUTOGIRO.MAIN_BANKS);
            break;
          case AUTOGIRO_STEPS.BANK_LIST:
            sendAnalyticsEvent(ANALYTICS_EVENTS.AUTOGIRO.BANK_LIST);
            break;
          case AUTOGIRO_STEPS.ACCOUNT_FORM:
            sendAnalyticsEvent(ANALYTICS_EVENTS.AUTOGIRO.ACCOUNT_FORM);
            break;
          case AUTOGIRO_STEPS.BILLING:
            sendAnalyticsEvent(ANALYTICS_EVENTS.AUTOGIRO.BILLING);
            break;
          case AUTOGIRO_STEPS.SUMMARY:
            sendAnalyticsEvent(ANALYTICS_EVENTS.AUTOGIRO.SUMMARY);
            break;
          case AUTOGIRO_STEPS.CLOSE_CONFIRMATION:
            sendAnalyticsEvent(ANALYTICS_EVENTS.AUTOGIRO.CLOSE_CONFIRMATION);
            break;
          case AUTOGIRO_STEPS.START_SIGN:
            sendAnalyticsEvent(ANALYTICS_EVENTS.AUTOGIRO.START_SIGN);
            break;
          default: break;
        }
        break;
      }
      case PAYMENT_TYPES.BETALINGSSERVICE: {
        switch (step) {
          case BETALINGSSERVICE_STEPS.ADD_BANK_ACCOUNT:
            sendAnalyticsEvent(ANALYTICS_EVENTS.BETALINGSSERVICE.ADD_BANK_ACCOUNT);
            break;
          case BETALINGSSERVICE_STEPS.CONNECTING_BANK:
            sendAnalyticsEvent(ANALYTICS_EVENTS.BETALINGSSERVICE.CONNECTING_BANK);
            break;
          case BETALINGSSERVICE_STEPS.ACCOUNT_SELECTION:
            sendAnalyticsEvent(ANALYTICS_EVENTS.BETALINGSSERVICE.ACCOUNT_SELECTION);
            break;
          case BETALINGSSERVICE_STEPS.MAIN_BANKS:
            sendAnalyticsEvent(ANALYTICS_EVENTS.BETALINGSSERVICE.MAIN_BANKS);
            break;
          case BETALINGSSERVICE_STEPS.BANK_LIST:
            sendAnalyticsEvent(ANALYTICS_EVENTS.BETALINGSSERVICE.BANK_LIST);
            break;
          case BETALINGSSERVICE_STEPS.ACCOUNT_FORM:
            sendAnalyticsEvent(ANALYTICS_EVENTS.BETALINGSSERVICE.ACCOUNT_FORM);
            break;
          case BETALINGSSERVICE_STEPS.BILLING:
            sendAnalyticsEvent(ANALYTICS_EVENTS.BETALINGSSERVICE.BILLING);
            break;
          case BETALINGSSERVICE_STEPS.SUMMARY:
            sendAnalyticsEvent(ANALYTICS_EVENTS.BETALINGSSERVICE.SUMMARY);
            break;
          case BETALINGSSERVICE_STEPS.CLOSE_CONFIRMATION:
            sendAnalyticsEvent(ANALYTICS_EVENTS.BETALINGSSERVICE.CLOSE_CONFIRMATION);
            break;
          case BETALINGSSERVICE_STEPS.START_SIGN:
            sendAnalyticsEvent(ANALYTICS_EVENTS.BETALINGSSERVICE.START_SIGN);
            break;
          default: break;
        }
        break;
      }
      case PAYMENT_TYPES.INVOICE: {
        switch (step) {
          case INVOICE_STEPS.BILLING_SELECTOR:
            sendAnalyticsEvent(ANALYTICS_EVENTS.INVOICE.BILLING_SELECTOR);
            break;
          case INVOICE_STEPS.BILLING_EDITOR:
            sendAnalyticsEvent(ANALYTICS_EVENTS.INVOICE.BILLING_EDITOR);
            break;
          default: break;
        }
        break;
      }
      case PAYMENT_TYPES.SWISH: {
        switch (step) {
          case SWISH_STEPS.WAIT:
            sendAnalyticsEvent(ANALYTICS_EVENTS.SWISH.WAIT);
            break;
          case SWISH_STEPS.RETRY:
            sendAnalyticsEvent(ANALYTICS_EVENTS.SWISH.RETRY);
            break;
          case SWISH_STEPS.CLOSE_CONFIRMATION:
            sendAnalyticsEvent(ANALYTICS_EVENTS.SWISH.CLOSE_CONFIRMATION);
            break;
          default: break;
        }
        break;
      }
      default: break;
    }
  }, [ selectedPaymentPlan, setDialogNavigationPath, setAppStep, sendAnalyticsEvent ]);

  return (
    <PaymentContext.Provider
      value={{
        setPaymentStep: setStep,
        swishError,
        setSwishError
      }}
    >
      {children}
    </PaymentContext.Provider>
  );
};

const PaymentConsumer = PaymentContext.Consumer;

const usePaymentContext = () => {
  const context = useContext(PaymentContext);

  if (context === undefined) {
    throw new Error('usePaymentContext must be used within a PaymentProvider');
  }

  return context;
};

export {
  PaymentContext, PaymentProvider, PaymentConsumer, usePaymentContext
};
